import {
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  FolderIcon,
} from '@heroicons/react/24/outline';
import { Trans, useTranslation } from 'react-i18next';
import RetentionShell, {
  RetentionRow,
} from '../RetentionShell.component';
import { useCancelSubscriptionContext } from '../CancelSubscription.context';

export type tRetentionModalProps = {
  isOpen: boolean;
};

/**
 * Group a count for display, tolerating a locale tag Intl will not accept.
 * The app's language codes are `es`, `en` and `br`, and `br` is Breton to Intl, not
 * Brazilian Portuguese — so passing it straight through either mis-groups or throws.
 */
function groupCount(value: number, locale: string | undefined): string {
  try {
    return value.toLocaleString(locale || undefined);
  } catch {
    return value.toLocaleString();
  }
}

/**
 * The opening step for a workspace that has activity: it leads with what the team has
 * actually done. All three figures were already fetched by the cancel hook for the
 * funnel analytics, so showing them costs no extra request.
 */
export default function RetentionModal(props: tRetentionModalProps) {
  const { isOpen } = props;
  const {
    isLoadingLeads,
    totalLeadsCount,
    activeFunnelsCount,
    sentMessagesCount,
    userNames,
    remainingUsersCount,
    handleKeepSubscription,
    handleContinue,
    handleClose,
  } = useCancelSubscriptionContext();
  const { t, i18n } = useTranslation('settings/SubscriptionManager');

  const stats = [
    {
      key: 'leads',
      labelKey: 'cancelSubscription.retention.statLeads',
      value: totalLeadsCount,
      icon: FolderIcon,
    },
    {
      key: 'messages',
      labelKey: 'cancelSubscription.retention.statMessages',
      value: sentMessagesCount,
      icon: ChatBubbleLeftRightIcon,
    },
    {
      key: 'funnels',
      labelKey: 'cancelSubscription.retention.statFunnels',
      value: activeFunnelsCount,
      icon: ChartBarIcon,
    },
  ];

  return (
    <RetentionShell
      isOpen={isOpen}
      heading={t('cancelSubscription.retention.activityHeading')}
      intro={t('cancelSubscription.retention.activitySubheading')}
      onKeep={handleKeepSubscription}
      onContinue={handleContinue}
      onClose={handleClose}
      isBusy={isLoadingLeads}
      footnote={
        !isLoadingLeads ? (
          userNames.length === 0 ? (
            t('cancelSubscription.retention.warningNoUsers')
          ) : remainingUsersCount > 0 ? (
            <Trans
              i18nKey="settings/SubscriptionManager:cancelSubscription.retention.warning"
              values={{
                userNames: userNames.join(', '),
                remainingCount: remainingUsersCount,
              }}
              components={{ bold: <strong /> }}
            />
          ) : (
            <Trans
              i18nKey="settings/SubscriptionManager:cancelSubscription.retention.warningNoRemaining"
              values={{ userNames: userNames.join(', ') }}
              components={{ bold: <strong /> }}
            />
          )
        ) : undefined
      }
    >
      {stats.map((stat, index) => (
        <RetentionRow
          key={stat.key}
          icon={stat.icon}
          label={t(stat.labelKey)}
          index={index}
          testId={`retention-stat-${stat.key}`}
          value={
            isLoadingLeads ? (
              <span className="h-6 w-16 animate-pulse rounded bg-neutral-300" />
            ) : (
              <span className="text-2xl font-bold text-neutral-800">
                {groupCount(stat.value, i18n.language)}
              </span>
            )
          }
        />
      ))}
    </RetentionShell>
  );
}
